import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "../lib/api";
import type { AuthPayload, OrganizationMembership } from "@hsh/shared";

interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  expiresAt: number;
}

interface AuthState {
  user: AuthPayload | null;
  tokens: TokenData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  updateOrganization: (org: OrganizationMembership | undefined) => void;
  updateTokens: (tokens: TokenData) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.login(email, password);
          const { user, accessToken, refreshToken, expiresIn } = response.data;

          const tokens: TokenData = {
            accessToken,
            refreshToken,
            expiresIn,
            expiresAt: Date.now() + expiresIn * 1000,
          };

          set({
            user: {
              userId: user._id,
              email: user.email,
              role: user.role,
              organization: user.organization,
            },
            tokens,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : "Login failed",
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        const { tokens } = get();
        try {
          await api.logout(tokens?.refreshToken);
        } catch {
          // Ignore logout errors
        }
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
        });
      },

      checkAuth: async () => {
        const { tokens } = get();
        if (!tokens) return;

        // Restore tokens to API client
        api.setTokens(tokens);

        try {
          const response = await api.getMe();
          set({ user: response.data, isAuthenticated: true });
        } catch {
          set({ user: null, tokens: null, isAuthenticated: false });
        }
      },

      updateOrganization: (org: OrganizationMembership | undefined) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, organization: org } });
        }
      },

      updateTokens: (tokens: TokenData) => {
        set({ tokens });
      },
    }),
    {
      name: "hsh-auth",
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.tokens) {
          api.setTokens(state.tokens);

          // Set up token refresh callback
          api.onTokensRefreshed((newTokens) => {
            state.updateTokens(newTokens);
          });

          // Set up auth error callback (logout on refresh failure)
          api.onAuthenticationError(() => {
            useAuthStore.getState().logout();
          });
        }
      },
    }
  )
);
